import React from "react";
import { View, KeyboardAvoidingView, TouchableOpacity } from "react-native";

import { isEqual, find, omit } from "lodash";
import moment from "moment";

import Utils, {
  hasItems,
  navigate,
  durationToHHmm,
  copyDeep,
  hhMMToDec,
} from "../../../helpers/utils";

import appStyles from "../../appStyles";
import Search from "../common/search-bar/Search";
import JournalDialog from "../common/JournalDialog";
import AppTheme from "../AppTheme";
import BottomButton from "../common/BottomButton";
import MenuEditOptionsPanel from "../common/MenuEditOptionsPanel";
import AppMessages from "../../../config/AppMessages";
import ReflectionList from "../reflection/list/ReflectionList";
import { ReflectionProvider } from "../reflection/ReflectionContext";
import ReportsFabButton from "./ReportsFabButton";
import Header from "../common/header/index";
import ReflectionHelper from "../reflection/ReflectionHelper";
import ReportNotes from "./notes/ReportNotes";
import ReportJournalsList from "./preview/ReportJournalsList";
import ReportUsersList from "./users/ReportUsersList";
import RecipientsDialog from "./users/RecipientsDialog";
import UsersHelper from "../users/UsersHelper";
import TimePickerDialog from "../common/TimePickerDialog";
import EditReportElementsView from "./EditReportElementsView";
import ElementsHelper from "../elements/ElementsHelper";
import appPages from "../../../appPages";
import SettingsHelper from "../more/SettingsHelper";
import ViewModes from "../reflection/header/viewModes";
import ExportIcon from "../common/ExportIcon";
import ReportsHelper from "./ReportsHelper";
import HiddenIcon from "../common/icons/integrations/HiddenIcon";

const SyncInProgressMessage = AppMessages.SyncInProgress;
const DATE_FORMAT = "YYYY-MM-DD";
const CURRENT_STEP = 1;
const PREFERENCES_KEY = "checked-report-elements";

export default class ReportsPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.useElements = props.useElements;

    this.isEditMode = !!props.isEditMode;

    this.steps = {
      users: 1,
      elements: 2,
      notes: 3,
      preview: 4,
      submit: 5,
    };

    this.state = {
      selectedMenuElement: null,
      selectedJournalGoal: null,
      pageTitle: "Report",
      report: {},
      reflections: [],
      currentStep: CURRENT_STEP,
      reportRecipients: [],
      viewMode: ViewModes.chartView,
    };
  }

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  componentDidMount = async () => {
    const {
      report,
      selectedDate = "2021-05-19",
      timeUnit = "week",
    } = this.getArgs() || {};

    const tu = timeUnit == "week" ? "isoWeek" : timeUnit;
    const reportDate = ["year", "month", "quarter"].includes(timeUnit)
      ? moment(selectedDate).format(DATE_FORMAT)
      : moment(selectedDate).startOf(tu).format(DATE_FORMAT);

    // Edit report
    if (report) {
      this.viewReport(report);

      // Create
    } else {
      this.setState(
        (state) => ({
          ...state,
          selectedDate: reportDate,
          timeUnit,
          currentStep: CURRENT_STEP,
        }),
        () => {
          this.loadReflections();
        },
      );
    }
  };

  // ------------------------------------- EDIT REPORT -------------------------------------
  viewReport = (report) => {
    const { reference_day, data, users, time_unit } = report;

    const reflectionData = JSON.parse(data);
    const allGems = ReportsHelper.getAllGems(
      reflectionData,
      reference_day,
      time_unit,
      this.isEditMode,
    );

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        selectedDate: moment(reference_day).format(DATE_FORMAT),
        currentStep: this.steps.preview,
        timeUnit: time_unit,
        report: {
          ...report,
          comment: report.description,
        },
        allGems,
        selectedJournals: allGems,
        reportRecipients: users,
      }),
      () => {
        this.setReflectionModel(
          reflectionData && reflectionData.children
            ? reflectionData
            : { children: reflectionData },
        );
      },
    );
  };

  // ------------------------------------- API -------------------------------------
  loadReflections = async () => {
    const oldReflectionData = this.controller.state?.reflection;

    if (oldReflectionData) {
      this.setReflectionModel(oldReflectionData);
    }

    // Check if newer version of data is available
    this.getReflectionDataFromApi();
  };

  getReflectionDataFromApi = () => {
    const { selectedDate, timeUnit } = this.state;

    const params = selectedDate
      ? {
          reflectionDate: selectedDate,
          time_unit: timeUnit,
          useElements: !!this.useElements,
        }
      : null;

    const oldReflectionData = this.controller.state?.reflection;

    this.setState((state) => ({
      ...state,
      timeUnit,
      isLoading: true,
      mariMessage: SyncInProgressMessage,
    }));

    this.controller.getApiData("reflection", params, (reflectionData) => {
      if (isEqual(oldReflectionData, reflectionData)) {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          mariMessage: "",
          term: "",
        }));
        return;
      }

      this.setReflectionModel(reflectionData);
    });
  };

  setReflectionModel = async (reflectionData) => {
    let reflections = reflectionData.children;

    if (reflections != null) {
      // Load report elements selected from previous report
      const reportElementIdsData = await SettingsHelper.loadData(
        this.controller,
        PREFERENCES_KEY,
      );

      let reportElementIds =
        typeof reportElementIdsData === "string"
          ? JSON.parse(reportElementIdsData || "{}")
          : reportElementIdsData || {};
      reflections.forEach((r) =>
        this._initCollapsedState(r, reportElementIds, null),
      );
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      reflections,
      mariMessage: "",
      term: "",
    }));
  };

  _computeElementId = (element) => {
    return element.parent_id
      ? `${element.parent_id}-${element.item_id}`
      : element.item_id;
  };

  _initCollapsedState = (element, reportElementIds, isParentSelected) => {
    element.isExpanded = element.item_type == "set";

    const storageElementId = this._computeElementId(element);
    const hasChildren = hasItems(element.children);

    // 2022.05.02 Elements should not show up in the report template if they have no hours associated with them for the week in question
    const hasHours = element.days && element.days[0].hours > 0;

    element.isSelected = reportElementIds
      ? reportElementIds.hasOwnProperty(storageElementId)
        ? reportElementIds[storageElementId] && hasHours // Checked or not based on saved preferences
        : isParentSelected && hasHours // New children elements are selected if parent is selected
      : hasHours; // No preferences => initially all elements are selected if hours > 0

    if (hasChildren) {
      element.children.forEach((child) => {
        child.parent_id = element.item_id;
        this._initCollapsedState(child, reportElementIds, element.isSelected);
      });

      // 2022.04.29 Init partial selected parent state from children
      element.selectedChildrenCount = (element.children || []).filter(
        (c) => c.isSelected,
      ).length;
    }
  };

  // ------------------------------------- EDIT MENU -------------------------------------
  getMenuOptions = (element) => {
    const hasNotes = hasItems(element.days) && element.days[0].gem?.mood;

    return [
      /* {
        text: "Edit Hours",
        action: this.showTimePicker,
      }, */
      {
        text: (hasNotes ? "Edit" : "Add") + " Journal Entry",
        action: this.toggleJournalDialog,
      },
      {
        text: "Remove Element from View",
        isDelete: true,
        action: this.removeElementFromView,
      },
    ];
  };

  showEditOptions = (selectedMenuElement) => {
    if (this.isEditMode) {
      this.setState((state) => ({ ...state, selectedMenuElement }));
    } else {
      this.showJournalNote(selectedMenuElement);
    }
  };

  closeEditOptions = () => {
    this.setState((state) => ({ ...state, selectedMenuElement: null }));
  };

  showJournalNote = (selectedMenuElement) => {
    let editDay = {};
    if (hasItems(selectedMenuElement.days)) {
      editDay = selectedMenuElement.days[0];
    }

    if (editDay && editDay.gem) {
      const { selectedDate, timeUnit } = this.state;

      let model = {
        ...selectedMenuElement,
        note: editDay?.gem?.text,
        mood: editDay?.gem?.mood,
        day: selectedDate,
        time_unit: timeUnit,
        gem_id: editDay?.gem?.gem_id,
        hours: editDay?.hours,
      };

      this.setState((state) => ({
        ...state,
        selectedMenuElement: null,
        selectedJournalGoal: model,
      }));
    }
  };

  // ------------------------------------- HOURS EDIT -------------------------------------
  showTimePicker = (element) => {
    // 2022.06.30 Disable clicking on hours while creating a report to change hours
    return;
    const { selectedMenuElement, currentStep } = this.state;

    if (!this.isEditMode) {
      return this.showJournalNote(element);
    }

    const selectedElement = selectedMenuElement || element;
    const dayIndex = 0;
    const day = hasItems(selectedElement.days)
      ? selectedElement.days[dayIndex]
      : {};

    if (currentStep > 2 || selectedElement.is_uncategorized) {
      return;
    }

    const { hours, minutes } = day.hours
      ? durationToHHmm(day.hours)
      : { hours: 0, minutes: 0 };

    selectedElement.dayIndex = dayIndex;
    selectedElement.time = `${hours}:${minutes}`;

    this.setState((state) => ({
      ...state,
      selectedMenuElement: null,
      selectedElement,
    }));
  };

  closeTimePicker = () => {
    this.setState((state) => ({
      ...state,
      selectedElement: null,
    }));
  };

  updateEndTime = (hhmm) => {
    const { selectedElement } = this.state;

    const newDecTime = hhMMToDec(hhmm);
    if (hasItems(selectedElement.days)) {
      const oldtime = selectedElement.days[selectedElement.dayIndex].hours;

      if (oldtime != newDecTime) {
        selectedElement.days[selectedElement.dayIndex].hours = newDecTime;
        selectedElement.days[selectedElement.dayIndex].is_modified = true;
      }
    }
  };

  removeElementFromView = () => {
    const { selectedMenuElement } = this.state;

    this.toggleElementSelection({ ...selectedMenuElement }, false);
    this.closeEditOptions();
  };

  // ------------------------------------- NOTES -------------------------------------
  _updateGemInfo = (elements, element_id, newGem) => {
    if (!hasItems(elements)) return;

    elements.forEach((element) => {
      // Save locally
      if (element.item_id == element_id) {
        if (element.timeline) {
          const gem = find(
            element.timeline.gems,
            (g) => g.gem_id == newGem.gem_id,
          );

          // Refresh gems for Step 3: notes
          if (gem) {
            gem.text = newGem.text;
            gem.mood = newGem.mood;
            gem.time_unit = "mana";
          } else {
            element.timeline.gems.push({
              ...newGem,
              day: this.state.selectedDate,
              time_unit: "mana",
            });
          }
        }

        // Update day gems for Step 2: elements selection
        if (hasItems(element.days)) {
          const oldGem = element.days[0].gem || {};
          element.days[0].gem = {
            ...oldGem,
            ...newGem,
            time_unit: this.state.timeUnit,
            day: this.state.selectedDate,
          };
        }
      } else {
        this._updateGemInfo(element.children, element_id, newGem);
      }
    });
  };

  saveJournalNotes = (gem) => {
    const { reflections, selectedJournalGoal } = this.state;
    const elementId = selectedJournalGoal?.item_id;

    this._updateGemInfo(reflections, elementId, gem);

    this.setState((state) => ({
      ...state,
      selectedJournalGoal: null,
    }));
  };

  toggleJournalDialog = () => {
    const { selectedMenuElement, timeUnit, selectedDate } = this.state;

    let model = null;
    if (selectedMenuElement) {
      let editDay = {};
      if (hasItems(selectedMenuElement.days)) {
        editDay = selectedMenuElement.days[0];
      }

      model = {
        ...selectedMenuElement,
        note: editDay?.gem?.text,
        mood: editDay?.gem?.mood,
        day: selectedDate,
        time_unit: timeUnit,
        gem_id: editDay?.gem?.gem_id,
        hours: editDay?.hours,
      };
    }

    this.setState((state) => ({
      ...state,
      selectedMenuElement: null,
      selectedJournalGoal: model,
    }));
  };

  updateOverallNotes = ({ report, selectedGemIds }) => {
    this.setState(
      (state) => ({
        ...state,
        report: { ...report },
        selectedGemIds,
      }),
      () => {
        this.changeStep(this.steps.preview);
      },
    );
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = (reflections, pattern) => {
    if (!hasItems(reflections)) return false;

    let shouldDisplay = false;
    reflections.forEach((r) => {
      const hasVisibileChildren = this.filterByKeyword(r.children, pattern);
      r.isHidden =
        r.item_type == "set" || hasVisibileChildren
          ? false
          : pattern
          ? r.name && !r.name.match(pattern)
          : false;

      if (!r.isHidden) shouldDisplay = true;
    });

    return shouldDisplay;
  };

  filterResults = ({ term }) => {
    try {
      let filteredReflections = this.state.reflections
        ? [...this.state.reflections]
        : [];

      const pattern = Utils.getSearchPattern(term);

      this.filterByKeyword(filteredReflections, pattern);

      this.setState((state) => ({
        ...state,
        isLoading: false,
        reflections: [...filteredReflections],
      }));
    } catch (e) {
      console.log("Failed parsing regexp");
      console.log(e);
    }
  };

  // ------------------------------------- NAV -------------------------------------
  goToReflectionDetail = (reflection) => {
    const { timeUnit, selectedDate, currentStep } = this.state;

    // Prevent access to reflection details in view mode
    if (currentStep > 2 || !this.isEditMode) return;

    let model = {
      edit_time_unit: timeUnit,
      edit_object: reflection.item_type || "time",
      edit_element_id: reflection.item_id,
      edit_day: selectedDate,
      edit_icon: reflection.icon,
      edit_name: reflection.name,
      element: { ...reflection },
    };

    navigate(this.controller, "rn:sets_reflection_detail", "changepage", {
      reflection: { ...model },
      fetchFromApi: true,
    });
  };

  onRecipientsSelected = (recipients) => {
    if (!recipients) return;

    this.setState((state) => ({
      ...state,
      reportRecipients: recipients,
      currentStep: this.steps["elements"],
    }));
  };

  goBack = () => {
    const { currentStep, report } = this.state;

    if (currentStep == this.steps.users || report.id) {
      if (report) {
        return this.controller.goBack();
      } else {
        return navigate(this.controller, appPages.reflectionPage);
      }
    }

    this.changeStep(currentStep - 1);
  };

  changeStep = (step) => {
    this.setState((state) => ({ ...state, currentStep: step }));

    if (step == this.steps.notes) {
      const { selectedDate, reflections, timeUnit } = this.state;

      const allGems = ReportsHelper.getAllGems(
        reflections,
        selectedDate,
        timeUnit,
        this.isEditMode,
      );

      this.setState((state) => ({ ...state, allGems }));
    }

    if (step == this.steps.preview) {
      this.previewReport();
    }

    if (step == this.steps.submit) {
      this.submitReport();
    }
  };

  // ------------------------------------- PREVIEW -------------------------------------
  _getPersonalElement = (elements = []) => {
    const personal = find(elements, (elem) => elem.item_id == "personal");

    return (
      personal || {
        name: "Personal Element",
        isExpanded: true,
        isSelected: true,
        item_type: "set",
        item_id: "personal",
        children: [],
      }
    );
  };

  previewReport = () => {
    const { reflections, allGems, selectedGemIds } = this.state;

    // Orphan elements will be included in Personal Element
    let personalElement = this._getPersonalElement(reflections);
    let reflectionsWithoutPersonal = reflections.filter(
      (elem) => elem.item_id != "personal",
    );

    const selectedReflections = this._getPreviewElements(
      reflectionsWithoutPersonal,
      selectedGemIds,
      personalElement,
    );

    const selectedElementIds = {};
    this._getSelectedElementIdsRec(selectedReflections, selectedElementIds);

    // Filter out already selected elements
    personalElement = this._filterPersonalElements(
      personalElement,
      selectedElementIds,
    );
    if (hasItems(personalElement.children)) {
      selectedReflections.push(personalElement);
    }

    const selectedJournals = allGems.filter((g) =>
      selectedGemIds.includes(g.gem_id),
    );

    this.setState((state) => ({
      ...state,
      selectedReflections,
      selectedJournals,
    }));
  };

  _filterPersonalElements = (personalElement = {}, selectedElementIds = {}) => {
    const personalElements = personalElement.children || [];

    // Unique top elements + skip all elements included in other groups
    const orphans = [];
    personalElements.forEach((c) => {
      if (!selectedElementIds[c.item_id]) {
        selectedElementIds[c.item_id] = 1;
        orphans.push(c);
      }
    });

    return {
      ...personalElement,
      children: orphans,
      days: [
        {
          hours: this._computePersonalHours(orphans),
        },
      ],
    };
  };

  _getSelectedElementIdsRec = (elements, selectedElementIds) => {
    if (!hasItems(elements)) return;

    elements.forEach((el) => {
      this._getSelectedElementIdsRec(el.children, selectedElementIds);
      selectedElementIds[el.item_id] = 1;
    });
  };

  _getPreviewElements = (elements, selectedGemIds, personalElement) => {
    let selected = [];
    copyDeep(elements).forEach((elem) => {
      const selectedChildren = elem.children
        ? this._getPreviewElements(
            elem.children,
            selectedGemIds,
            personalElement,
          )
        : [];

      if (hasItems(selectedChildren) || elem.isSelected) {
        if (elem.isSelected) {
          // Keep only selected journals
          if (elem.timeline && hasItems(elem.timeline.gems)) {
            elem.timeline.gems = elem.timeline.gems.filter((g) =>
              selectedGemIds.includes(g.gem_id),
            );
          }

          // Removed unused data
          delete elem.selectedChildrenCount;
          delete elem.unchecked_children;
          if (elem.timeline) {
            delete elem.timeline.days;
            delete elem.timeline.moods;
            delete elem.timeline.hours;
          }

          selected.push({
            ...elem,
            children: selectedChildren,
          });
        } else {
          selectedChildren.forEach((c) => {
            const existing = personalElement.children.find(
              (e) => e.item_id == c.item_id,
            );
            if (!existing) personalElement.children.push(c);
          });
        }
      }
    });

    return selected;
  };

  _computePersonalHours = (personalElements = []) => {
    const hours = {};
    this._getUniqueElementHours(personalElements, hours);
    return Object.values(hours).reduce((acc, h) => acc + h, 0);
  };

  _getUniqueElementHours = (personalElements = [], hours = {}) => {
    if (!hasItems(personalElements)) return;

    personalElements.forEach((c) => {
      // Skip parents (=sum of children hours)
      if (hasItems(c.children)) {
        this._getUniqueElementHours(c.children, hours);
      } else if (c.item_id) {
        hours[c.item_id] = hasItems(c.days) ? 1 * c.days[0]?.hours : 0;
      }
    });
  };

  // ------------------------------------- SUBMIT -------------------------------------
  buildLeavesDuration = (data, leaves) => {
    if (data && data.length) {
      data.forEach((c) => this.buildLeavesDuration(c, leaves));
    } else if (data.children && data.children.length > 0) {
      this.buildLeavesDuration(data.children, leaves);
    } else if (data.days && data.days.length > 0) {
      leaves[data.item_id] = data.days[0].hours * 60;
    }
  };

  _saveReportCheckedElements = (elements) => {
    let data = { reportElementIds: {} };
    this._getReportElementIds(elements, data, true);

    // Save unchecked report elements ids
    SettingsHelper.storeData(
      this.controller,
      PREFERENCES_KEY,
      JSON.stringify(data.reportElementIds),
    );
  };

  submitReport = () => {
    const {
      selectedReflections,
      reportRecipients,
      timeUnit,
      selectedDate,
      report = {},
      reflections,
    } = this.state;
    const { mood, comment, id } = report;

    let leaves = {};
    this.buildLeavesDuration(selectedReflections, leaves);
    let totalMinutes = 0;
    Object.values(leaves).forEach((d) => (totalMinutes += d));

    // Save selected elements for next report
    this._saveReportCheckedElements(reflections);

    let action = {
      action: "save",
      target: "report",
      report_id: id,
      sender: UsersHelper.getAliasForReport(this.controller, reportRecipients),
      to: reportRecipients.map((r) => r.email),
      data: selectedReflections,
      minutes: totalMinutes,
      reference_day: selectedDate,
      time_unit: timeUnit,
      mood: mood || 5,
      comment: comment || "initial report",
    };
    this.setState((state) => ({ ...state, isLoading: true }));

    this.controller.cardActionWithCallback(action, () => {
      this.setState((state) => ({ ...state, isLoading: false }));
      this.controller.navigate(appPages.reflectionPage);
    });
  };

  // ------------------------------------- GEMS -------------------------------------
  addReportNotes = () => {
    navigate(this.controller, "report_notes");
  };

  // ------------------------------------- SELECTION -------------------------------------
  toggleElementSelection = (selectedElement, isSelected = false) => {
    const { reflections } = this.state;

    for (const item of reflections) {
      this._toggleElement(item, selectedElement, isSelected);
    }

    this.setState((state) => ({ ...state, reflections: [...reflections] }));
  };

  _toggleElement = (element, selectedElement, isSelected) => {
    if (selectedElement.item_id == element.item_id) {
      if (!hasItems(element.children)) {
        element.isSelected = isSelected;
        return;
      }

      // 05.14.2021
      // 1st tap on top level element selects only top-level major element (deselect all children)
      // 2nd tap deselects all including the top level element,
      // 3rd taps goes back to original state (all selected).
      // When Major element is collapsed, 1st tap deselects parent and all children
      if (!selectedElement.isExpanded && hasItems(element.children)) {
        element.isSelected = isSelected;
      } else if (!isSelected) {
        if (!element.unchecked_children) {
          element.isSelected = true;
          element.unchecked_children = true;
        } else {
          element.isSelected = false;
          element.unchecked_children = false;
        }
      } else {
        element.isSelected = true;
      }

      this._toggleChildren(element.children, isSelected);
    } else {
      if (hasItems(element.children)) {
        for (const child of element.children) {
          this._toggleElement(child, selectedElement, isSelected);
        }
      }
    }

    element.selectedChildrenCount = (element.children || []).filter(
      (c) => c.isSelected,
    ).length;
  };

  _toggleChildren = (children, isSelected) => {
    if (!hasItems(children)) return;

    children.forEach((c) => {
      c.isSelected = isSelected;
      if (hasItems(c.children)) {
        this._toggleChildren(c.children, isSelected);
        c.selectedChildrenCount = c.children.filter((c) => c.isSelected).length;
      }
    });
  };

  // ------------------------------------- RECIPIENTS -------------------------------------
  toggleRecipientsDialog = (isOpen) => {
    this.setState((state) => ({ ...state, showRecipients: isOpen }));
  };

  // ------------------------------------- CUSTOMIZE -------------------------------------
  customizeElements = () => {
    const { reflections } = this.state;

    let data = { selected: [], all: [] };
    this._getReportElementIds(reflections, data);

    this.setState((state) => ({
      ...state,
      selectedElementIds: data.selected,
      allElementIds: data.all,
    }));
  };

  _getReportElementIds = (elements, data, saveForLater = false) => {
    if (!hasItems(elements)) return;

    if (!data.all) data.all = [];
    if (!data.selected) data.selected = [];
    if (!data.reportElementIds) data.reportElementIds = {};

    elements.forEach((elem) => {
      data.all.push(elem.item_id);

      // Selected elements
      if (!this.isEditMode || elem.isSelected) {
        data.selected.push(elem.item_id);
      }

      // Keep track of checked elements for future report
      if (saveForLater) {
        const storageElementId = this._computeElementId(elem);
        data.reportElementIds[storageElementId] = elem.isSelected;
      }

      this._getReportElementIds(elem.children, data, saveForLater);
    });
  };

  _getReportItemModel = (element) => {
    return {
      item_id: element.id,
      name: element.name,
      icon: element.icon,
      color: element.color,
      isExpanded: true,
      isSelected: true,
      days: [{ hours: 0 }, { hours: 0 }, { hours: 0 }],
      item_type: element.is_core == 1 ? "set" : "activity",
      parents: element.parents,
      children: [],
    };
  };

  _insertElementInTree = (elements, model) => {
    const parentIds = model.parents;
    if (!hasItems(elements) || !hasItems(parentIds)) return;

    parent = elements.find((elem) => parentIds.includes(elem.item_id));
    if (parent) {
      if (!hasItems(parent.children)) parent.children = [];
      parent.children.push(model);
      model.hasParent = true;
      return;
    }

    elements.forEach((elem) => {
      this._insertElementInTree(elem.children, model);
    });
  };

  updateSelectedElements = (newSelectedIds = []) => {
    const { reflections, allElementIds } = this.state;

    let newElements = reflections;

    // Toggle selection for old elements
    this._initElementsSelection(newElements, newSelectedIds);

    // New element ids selected
    const newIds = newSelectedIds.filter((id) => !allElementIds.includes(id));

    if (hasItems(newIds)) {
      const personalElement = this._getPersonalElement(newElements);
      personalElement.children = personalElement.children.filter(
        (elem) => !elem.isHidden,
      );

      // Skip personal as we'll add the new updated model later
      newElements = newElements.filter((elem) => elem.item_id != "personal");

      // Add each element either top level (set), inside its parent or inside Personal
      newIds.forEach((elementId) => {
        const element = ElementsHelper.findById(this.controller, elementId);

        if (element) {
          const model = this._getReportItemModel(element);

          // Top parent
          if (model.item_type == "set") {
            newElements.push(model);
          } else {
            // Find parent to show it nested under
            this._insertElementInTree(newElements, model);

            // No parent found => add to personal
            if (!model.hasParent) {
              personalElement.children.push(model);
            }
          }
        }
      });

      // Show Personal only if it has children
      if (hasItems(personalElement.children)) {
        newElements.push(personalElement);
      }
    }

    this.setState((state) => ({
      ...state,
      selectedElementIds: null,
      reflections: newElements,
    }));
  };

  _initElementsSelection = (elements, selectedIds = []) => {
    if (!hasItems(elements)) return;

    elements.forEach((elem) => {
      //elem.isSelected = elem.isSelected || selectedIds.includes(elem.item_id);
      elem.isSelected = selectedIds.includes(elem.item_id);
      elem.isHidden = !elem.isSelected && elem.days && !elem.days[0].hours;

      this._initElementsSelection(elem.children, selectedIds);
    });
  };

  toggleForwardMode = (isForwardMode) => {
    this.setState((state) => ({ ...state, isForwardMode }));
  };

  markAsUnread = () => {
    const { report } = this.state;

    const action = {
      action: "update",
      target: "report",
      id: report.id,
      was_read: false,
    };

    this.setState((state) => ({
      ...state,
      mariMessage: AppMessages.ReportMarkedAsUnread,
    }));

    console.log("Mark Report As Unread", action);
    //this.controller.cardActionWithCallback(action, () => {
      setTimeout(() => {
        this.setState((state) => ({ ...state, mariMessage: "" }));
        this.goBack();
      }, 2000);
    //});
  };

  forwardReport = (recipients) => {
    const { report } = this.state;
    if (!hasItems(recipients)) return;

    // Clean
    const newReport = omit(report, [
      "data",
      "datetime_updated",
      "description",
      "email_user",
      "id_master",
      "id",
      "is_active",
      "is_seen",
      "merged_ids",
      "merged_master_ids",
      "name",
      "receiver_nickname",
      "receiver",
      "report_id",
      "send_time",
      "send_tz",
      "user_nickname",
      "users",
      "wasReceivedReport",
    ]);

    const action = {
      target: "report",
      action: "save",
      ...newReport,
      reference_day: moment(newReport.reference_day).format(DATE_FORMAT),
      data: JSON.parse(report.data),
      sender: UsersHelper.getAliasForReport(this.controller, recipients),
      to: recipients.map((r) => r.email),
      was_forwarded: true,
    };

    console.log("Forward Report", action);
    this.controller.cardActionWithCallback(action, () => {
      this.toggleForwardMode(false);
    });
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      allGems,
      currentStep,
      isLoading,
      mariMessage,
      reflections,
      report,
      reportRecipients,
      selectedDate,
      selectedElement,
      selectedGemIds,
      selectedJournalGoal,
      selectedJournals,
      selectedMenuElement,
      selectedReflections,
      viewMode,
      selectedElementIds,
      showRecipients,
      timeUnit,
      isForwardMode,
    } = this.state;

    const availableReflections =
      !report.id && currentStep >= this.steps.preview // show only selected elements
        ? selectedReflections
        : reflections != null
        ? reflections.filter((r) => !r.isHidden)
        : {};

    const minutesInterval =
      this.controller.state?.settings?.interval_duration || 1;

    const pageTitle = hasItems(reportRecipients)
      ? UsersHelper.getUsersHeaderText(
          reportRecipients,
          `Report ${report.wasReceivedReport ? "from " : "for "}`,
        )
      : report.user_nickname;

    const buttonText = currentStep < 4 ? "Next" : `Submit Report`;
    const intervalText = ReflectionHelper.getPeriodDisplayText(
      selectedDate,
      timeUnit,
    );

    // Show forward option only for submitted reports
    const showForwardIcon = viewMode && !report.wasReceivedReport;
    const showUnreadIcon = viewMode && report.wasReceivedReport;

    const HeaderComp = (
      <Header
        title={pageTitle}
        onTitlePressed={() => this.toggleRecipientsDialog(true)}
        subTitle={intervalText}
        controller={this.controller}
        isLoading={isLoading}
        goBack={this.goBack}
        iconTag={
          showForwardIcon ? (
            <ExportIcon
              width={20}
              height={20}
              onPress={() => this.toggleForwardMode(true)}
            />
          ) : showUnreadIcon ? (
            <TouchableOpacity onPress={this.markAsUnread}>
              <HiddenIcon
                width={20}
                height={20}
                color={"#FFF"}
                color2={"#FFF"}
              />
            </TouchableOpacity>
          ) : null
        }
        rightText={
          currentStep == this.steps.elements
            ? "Elements to share"
            : currentStep == this.steps.notes
            ? "Journals to share"
            : ""
        }
      />
    );

    return selectedElementIds ? (
      <EditReportElementsView
        controller={this.controller}
        selectedElementIds={selectedElementIds}
        goBack={this.updateSelectedElements}
      />
    ) : (
      <ReflectionProvider
        value={{
          controller: this.controller,
          totalCount: availableReflections?.length,
          viewMode,
          timeUnit,
          isReportMode: true,
          reportStep: currentStep,
          onHoursSelected: this.showTimePicker,
          showEditOptions: this.showEditOptions,
          onElementSelected: this.showEditOptions,
          onElementPressed: this.goToReflectionDetail,
          onHoursLongPressed: this.showEditOptions,
          toggleElementSelection: this.toggleElementSelection,
        }}
      >
        {/* Recipients */}
        {showRecipients ? (
          <RecipientsDialog
            recipients={reportRecipients}
            close={() => this.toggleRecipientsDialog(null)}
          />
        ) : null}

        {currentStep == this.steps.users || isForwardMode ? (
          <ReportUsersList
            controller={this.controller}
            title={isForwardMode ? "Forward Report" : "Choose recipient"}
            onRecipientsSelected={
              isForwardMode ? this.forwardReport : this.onRecipientsSelected
            }
          />
        ) : currentStep == this.steps.notes ? (
          <ReportNotes
            gems={allGems}
            selectedJournalIds={selectedGemIds}
            report={report}
            timeUnit={timeUnit}
            updateOverallNotes={this.updateOverallNotes}
            header={HeaderComp}
            controller={this.controller}
          />
        ) : (
          <View
            style={[
              appStyles.container,
              { backgroundColor: AppTheme.containerBg },
            ]}
          >
            <KeyboardAvoidingView
              style={[appStyles.kbViewNoFooter]}
              behavior="height"
            >
              {HeaderComp}

              <ReflectionList
                reflections={availableReflections}
                controller={this.controller}
                source={"reflection"}
              >
                {/* Preview selected journals */}
                {currentStep == this.steps.preview ? (
                  <ReportJournalsList
                    report={report}
                    journals={selectedJournals}
                    controller={this.controller}
                    hideCommentsIcon={!report?.id} // Hide comment icon when creating a report
                  />
                ) : null}
              </ReflectionList>

              {!report.id ? (
                <View style={[appStyles.footerOptions]}>
                  <Search
                    showIcon={false}
                    controller={this.controller}
                    placeholder="Search elements"
                    filterResults={this.filterResults}
                    style={{
                      marginBottom: 34,
                      width: "96%",
                      marginHorizontal: "2%",
                    }}
                  />
                </View>
              ) : null}
            </KeyboardAvoidingView>

            <View style={appStyles.footerOptions}>
              <ReportsFabButton
                style={!report.id ? { bottom: 100 } : { bottom: 0 }}
                customizeElements={
                  currentStep == this.steps.elements
                    ? this.customizeElements
                    : null
                }
                mariOptions={{
                  mariMessage,
                }}
              />

              {!report.id ? (
                <BottomButton
                  buttonText={buttonText}
                  style={{ paddingHorizontal: 16, height: 40 }}
                  onPress={() => this.changeStep(currentStep + 1)}
                />
              ) : null}
            </View>

            {/* Menu options */}
            {selectedMenuElement ? (
              <MenuEditOptionsPanel
                isPanelActive={!!selectedMenuElement}
                close={this.closeEditOptions}
                options={this.getMenuOptions(selectedMenuElement)}
              />
            ) : null}

            {/* Journal popup */}
            {selectedJournalGoal ? (
              <JournalDialog
                type={"report"}
                item={selectedJournalGoal}
                controller={this.controller}
                close={() => this.toggleJournalDialog(null)}
                onJournalComplete={this.saveJournalNotes}
                isViewOnly={!this.isEditMode}
              />
            ) : null}

            {/* TimePicker popup */}
            <TimePickerDialog
              value={selectedElement?.time}
              isOpen={!!selectedElement}
              minutesInterval={minutesInterval}
              use24hFormat
              pickDuration
              hourDigits={3}
              debounceDelay={500}
              onConfirm={this.updateEndTime}
              onClose={() => this.closeTimePicker(false)}
            />
          </View>
        )}
      </ReflectionProvider>
    );
  }
}
